import { createActionCreators, createReducerFunction, ImmerReducer } from 'immer-reducer'
import { Progress } from 'modules/types'
import { arrToDict, getIds } from 'modules/utils/helpers'
import { LIST_PAGE_SIZE } from 'modules/constants'
import { AddError, FetchError, RemoveError, UpdateError } from 'modules/domain/types'
import {
  DigitalAsset,
  DigitalAssetDTO,
  DigitalAssetListRequestFilter,
  DigitalAssetListRequestSorting,
  DigitalAssetMetaData,
  DigitalAssetsState,
  DraftDigitalAsset,
  DigitalAssetEditDTO,
} from './types'
import { UploadCompanyType } from 'types/entities'

const initialState: DigitalAssetsState = {
  items: {},
  meta: {},
  ids: [],

  listFetchProgress: Progress.IDLE,
  listFetchError: null,
  listFetchNextProgress: Progress.IDLE,
  listFetchNextError: null,
  itemFetchProgress: Progress.IDLE,
  itemFetchError: null,
  addProgress: Progress.IDLE,
  addError: null,
  addErrorDetail: '',
  updateProgress: Progress.IDLE,
  updateError: null,
  updateErrorDetail: '',
  removeProgress: Progress.IDLE,
  removeError: null,
  itemMetaDataFetchProgress: Progress.IDLE,
  itemMetaDataFetchError: null,

  filter: {},
  sorting: {},
  page: 1,
  total: 0,
  pageSize: LIST_PAGE_SIZE,
  drafts: [],
  uploadCompanyType: UploadCompanyType.Producer,
}

class DigitalAssetsReducer extends ImmerReducer<DigitalAssetsState> {
  listRequested(params: {
    filter?: DigitalAssetListRequestFilter
    sorting?: DigitalAssetListRequestSorting
    page?: number
  }) {
    this.draftState.listFetchProgress = Progress.WORK
    this.draftState.listFetchError = null
    if (params.filter) this.draftState.filter = params.filter
    if (params.sorting) this.draftState.sorting = params.sorting
    this.draftState.page = typeof params.page === 'undefined' ? this.draftState.page : params.page
  }

  uploadCompanyTypeSet(uploadCompanyType: UploadCompanyType) {
    this.draftState.uploadCompanyType = uploadCompanyType
  }

  listRequestSucceed(list: DigitalAsset[], total: number, page: number) {
    this.draftState.listFetchProgress = Progress.SUCCESS
    this.draftState.total = total
    this.draftState.page = page
    this.draftState.items = arrToDict(list)
    this.draftState.meta = arrToDict(
      list.map((item) => ({
        id: item.id,
        fetchProgress: Progress.SUCCESS,
        fetchError: null,
        removeProgress: Progress.IDLE,
        removeError: null,
        updateProgress: Progress.IDLE,
        updateError: null,
      })),
    )
    this.draftState.ids = getIds(list)
  }

  listRequestFailed(error: FetchError) {
    this.draftState.listFetchProgress = Progress.ERROR
    this.draftState.listFetchError = error
  }

  itemRequested(id: string) {
    this.draftState.itemFetchProgress = Progress.WORK
    const meta = {
      id,
      updateProgress: Progress.IDLE,
      updateError: null,
      removeProgress: Progress.IDLE,
      removeError: null,
    }
    this.draftState.meta[id] = {
      ...meta,
      ...this.draftState.meta[id],
      fetchProgress: Progress.WORK,
      fetchError: null,
    }
  }

  itemRequestSucceed(digitalAsset: DigitalAsset) {
    this.draftState.itemFetchProgress = Progress.SUCCESS
    this.draftState.items[digitalAsset.id] = digitalAsset

    if (this.draftState.meta[digitalAsset.id]) {
      this.draftState.meta[digitalAsset.id].fetchProgress = Progress.SUCCESS
      this.draftState.meta[digitalAsset.id].fetchError = null
    } else {
      this.draftState.meta[digitalAsset.id] = {
        id: digitalAsset.id,
        updateProgress: Progress.IDLE,
        updateError: null,
        removeProgress: Progress.IDLE,
        removeError: null,
        fetchProgress: Progress.SUCCESS,
        fetchError: null,
      }
    }
  }

  itemRequestFailed(id: string, error: FetchError) {
    this.draftState.itemFetchProgress = Progress.ERROR
    this.draftState.meta[id].fetchProgress = Progress.ERROR
    this.draftState.meta[id].fetchError = error
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addRequested(dto: DigitalAssetDTO, file: File) {
    this.draftState.addProgress = Progress.WORK
    this.draftState.addError = null
  }

  addSucceed(digitalAsset: DigitalAsset) {
    this.draftState.addProgress = Progress.SUCCESS
    this.draftState.items[digitalAsset.id] = digitalAsset
    this.draftState.meta[digitalAsset.id] = {
      id: digitalAsset.id,
      fetchProgress: Progress.SUCCESS,
      fetchError: null,
      updateProgress: Progress.IDLE,
      updateError: null,
      removeProgress: Progress.IDLE,
      removeError: null,
    }
    // Add the new asset ID to the ids array so it appears in the table
    if (!this.draftState.ids.includes(digitalAsset.id)) {
      this.draftState.ids = [digitalAsset.id, ...this.draftState.ids]
    }
  }

  addFailed(type: AddError, detail: string | undefined) {
    this.draftState.addProgress = Progress.ERROR
    this.draftState.addError = type

    if (detail) this.draftState.addErrorDetail = detail
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  updateRequested(id: string, digitalAsset: Partial<DigitalAssetEditDTO>) {
    this.draftState.updateProgress = Progress.WORK
    this.draftState.meta[id].updateProgress = Progress.WORK
    this.draftState.meta[id].updateError = null
  }

  updateSucceed(digitalAsset: DigitalAsset) {
    this.draftState.items[digitalAsset.id] = digitalAsset
    this.draftState.updateProgress = Progress.SUCCESS
    this.draftState.meta[digitalAsset.id].updateProgress = Progress.SUCCESS
    this.draftState.meta[digitalAsset.id].updateError = null
  }

  updateFailed(id: string, type: UpdateError, detail: string | undefined) {
    this.draftState.meta[id].updateProgress = Progress.ERROR
    this.draftState.meta[id].updateError = type
    this.draftState.updateProgress = Progress.ERROR
    this.draftState.updateError = type

    if (detail) {
      this.draftState.meta[id].updateErrorDetail = detail
      this.draftState.updateErrorDetail = detail
    }
  }

  softDeleteRequested(_ids: string[]) {
    this.draftState.updateProgress = Progress.WORK
  }

  softDeleteSucceed(_ids: string[]) {
    this.draftState.updateProgress = Progress.SUCCESS
  }

  softDeleteFailed(_ids: string[], _error: RemoveError) {
    this.draftState.updateProgress = Progress.ERROR
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  restoreRequested(ids: string[]) {
    this.draftState.updateProgress = Progress.WORK
    ids.forEach((id) => {
      if (this.draftState.meta[id]) {
        this.draftState.meta[id].updateProgress = Progress.WORK
        this.draftState.meta[id].updateError = null
      }
    })
  }

  restoreSucceed(ids: string[]) {
    this.draftState.updateProgress = Progress.SUCCESS
    ids.forEach((id) => {
      if (this.draftState.meta[id]) {
        this.draftState.meta[id].updateProgress = Progress.SUCCESS
        // Update the status to active instead of deleted
        if (this.draftState.items[id]) {
          this.draftState.items[id].status = 'active' as any
        }
      }
    })
  }

  restoreFailed(ids: string[], error: RemoveError) {
    this.draftState.updateProgress = Progress.ERROR
    ids.forEach((id) => {
      if (this.draftState.meta[id]) {
        this.draftState.meta[id].updateProgress = Progress.ERROR
        this.draftState.meta[id].updateError = error
      }
    })
  }

  permanentDeleteRequested(ids: string[]) {
    this.draftState.removeProgress = Progress.WORK
    ids.forEach((id) => {
      if (this.draftState.meta[id]) {
        this.draftState.meta[id].removeProgress = Progress.WORK
        this.draftState.meta[id].removeError = null
      }
    })
  }

  permanentDeleteSucceed(ids: string[]) {
    this.draftState.removeProgress = Progress.SUCCESS
    ids.forEach((id) => {
      delete this.draftState.items[id]
      delete this.draftState.meta[id]
      const i = this.draftState.ids.findIndex((item) => item === id)
      if (-1 !== i) {
        const ids = this.draftState.ids
        this.draftState.ids = [...ids.slice(0, i), ...ids.slice(i + 1)]
      }
    })
  }

  permanentDeleteFailed(ids: string[], error: RemoveError) {
    this.draftState.removeProgress = Progress.ERROR
    ids.forEach((id) => {
      if (this.draftState.meta[id]) {
        this.draftState.meta[id].removeProgress = Progress.ERROR
        this.draftState.meta[id].removeError = error
      }
    })
  }

  filterUpdated(filter: DigitalAssetListRequestFilter) {
    this.draftState.filter = filter
    this.draftState.listFetchProgress = Progress.WORK
  }

  sortingUpdated(sorting: DigitalAssetListRequestSorting) {
    this.draftState.sorting = sorting
    this.draftState.listFetchProgress = Progress.WORK
  }

  filterHasBeenReset() {
    this.draftState.filter = {}
    this.draftState.listFetchProgress = Progress.WORK
  }

  sortingHasBeenReset() {
    this.draftState.sorting = {}
    this.draftState.listFetchProgress = Progress.WORK
  }

  listRequestedNext(page: number) {
    this.draftState.page = page
    this.draftState.listFetchNextProgress = Progress.WORK
    this.draftState.listFetchError = null
  }

  listRequestNextSucceed(list: DigitalAsset[], total: number) {
    this.draftState.listFetchNextProgress = Progress.SUCCESS
    this.draftState.total = total
    this.draftState.items = { ...this.draftState.items, ...arrToDict(list) }
    this.draftState.ids = [...this.draftState.ids, ...getIds(list)]
  }

  listRequestNextFailed(error: FetchError) {
    this.draftState.listFetchNextProgress = Progress.ERROR
    this.draftState.listFetchNextError = error
  }

  downloadFileRequested(payload: [string, string?]) {
    const [id] = payload
    this.draftState.meta[id].downloadProgress = Progress.WORK
    this.draftState.meta[id].downloadError = null
  }

  downloadFileSucceed(id: string) {
    this.draftState.meta[id].downloadProgress = Progress.SUCCESS
    this.draftState.meta[id].downloadError = null
  }

  downloadFileFailed(id: string, error: FetchError) {
    this.draftState.meta[id].downloadProgress = Progress.ERROR
    this.draftState.meta[id].downloadError = error
  }

  addLinkRequested(_dto: DigitalAssetDTO) {
    this.draftState.addProgress = Progress.WORK
    this.draftState.addError = null
  }

  addLinkSucceed(linkAsset: DigitalAsset) {
    this.draftState.addProgress = Progress.SUCCESS
    this.draftState.items[linkAsset.id] = linkAsset
    this.draftState.meta[linkAsset.id] = {
      id: linkAsset.id,
      fetchProgress: Progress.SUCCESS,
      fetchError: null,
      updateProgress: Progress.IDLE,
      updateError: null,
      removeProgress: Progress.IDLE,
      removeError: null,
    }
    // Add the new asset ID to the ids array so it appears in the table
    if (!this.draftState.ids.includes(linkAsset.id)) {
      this.draftState.ids = [linkAsset.id, ...this.draftState.ids]
    }
  }

  addLinkFailed(type: AddError, detail: string | undefined) {
    this.draftState.addProgress = Progress.ERROR
    this.draftState.addError = type

    if (detail) this.draftState.addErrorDetail = detail
  }

  selectedFileDraftRequested(_ids: DraftDigitalAsset[]) {}

  selectedFileDraftSetter(files: DraftDigitalAsset[]) {
    this.draftState.drafts = files
  }

  itemMetaDataRequested(_id: string, _digitalAsset: DigitalAsset) {
    this.draftState.itemMetaDataFetchProgress = Progress.WORK
  }

  itemMetaDataRequestSucceed(metaData: DigitalAssetMetaData) {
    this.draftState.itemMetaDataFetchProgress = Progress.SUCCESS
    this.draftState.items[metaData.obj_id].metadata = metaData
    this.draftState.itemMetaDataFetchError = null
  }

  itemMetaDataRequestFailed(error: FetchError) {
    this.draftState.itemMetaDataFetchProgress = Progress.ERROR
    this.draftState.itemMetaDataFetchError = error
  }
}

export const DigitalAssetActions = createActionCreators(DigitalAssetsReducer)
export default DigitalAssetActions
export const reducer = createReducerFunction(DigitalAssetsReducer, initialState)
